import { Divider, Text } from "@ui-kitten/components";
import FilterIcon from "components/FilterIcon";
import MainContainer from "components/MainContainer";
import { setSelectedSubscription } from "features/slices/reservationFlowSlice";
import { EventAvailabilityDetails, SubscriptionDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import dayjs from "dayjs";
import { useFetchEventAvailablitiesQuery } from "queries/ticket.queries";
import React, { useEffect, useState, useMemo } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import toast from "utils/toast";
import Filters from "./components/Filters";
import PlanPackageSelector from "./components/PlanPackageSelector";
import TDCDialog from "./components/TDCDialog";
import ScheduleItemList from "./ScheduleItemList";
import i18n from "plugins/localization";
import {
  getUniqSubscriptions,
  sortVoucherSubsByUnusedVouchers,
} from "utils/subscriptionUtils";

const MONTH_FILTER_FORMAT = "MMM";
const DAY_FILTER_FORMAT = "ddd";

export const NoEventsText = () => (
  <Text
    style={{
      fontSize: 24,
      fontWeight: "700",
      color: "#9e9e9e",
      textAlign: "center",
    }}
  >
    {i18n.t("screens.Schedule.noEligibleEventsBanner")}
  </Text>
);

export default function Schedule() {
  const [tenantName, setTenantName] = useState("");
  const [emptySchedule, setEmptySchedule] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [TDCDialogVisible, setTDCDialogVisible] = useState(false);

  const dispatch = useAppDispatch();
  const { account } = useAccount();

  const { selectedSubscription, isChangingSeats, eventFilters } =
    useAppSelector((state) => state.reservationFlow);
  const {
    data: schedule = [],
    isFetching,
    refetch: fetchEventAvailablities,
  } = useFetchEventAvailablitiesQuery({
    accountId: account?.id,
    planPackageId: selectedSubscription?.planPackage.id,
  });
  if (!account) return null;

  const uniqSubscriptions = useMemo(() => {
    return getUniqSubscriptions(account.subscriptions)
      .sort((a, b) => a.id - b.id) // sort subscriptions by id, looks hierarchically especially when there are child plans
      .sort(
        // https://github.com/FanRally/fanrally-core/issues/1271
        sortVoucherSubsByUnusedVouchers
      );
  }, [account.subscriptions]);
  const planPackageOperable = selectedSubscription?.operative || false;

  const filterSchedule = (schedule: EventAvailabilityDetails[]) => {
    const filterByEventType = (item: EventAvailabilityDetails) => {
      return eventFilters.eventTypes.length === 0
        ? true
        : eventFilters.eventTypes.includes(item.eventType);
    };

    const filterByMonths = (item: EventAvailabilityDetails) => {
      if (eventFilters.months.length === 0) return true;
      const itemMonth = dayjs(item.date)
        .tz(item.timezone)
        .format(MONTH_FILTER_FORMAT);
      return eventFilters.months.includes(itemMonth);
    };

    const filterByDay = (item: EventAvailabilityDetails) => {
      const itemDay = dayjs(item.date)
        .tz(item.timezone)
        .format(DAY_FILTER_FORMAT);
      return eventFilters.days.length === 0
        ? true
        : eventFilters.days.includes(itemDay);
    };

    return schedule
      .filter(filterByEventType)
      .filter(filterByMonths)
      .filter(filterByDay);
  };

  // gets called when initial mount and user adds/upgrades another membership
  useEffect(() => {
    if (selectedSubscription === null) {
      dispatch(setSelectedSubscription(uniqSubscriptions[0]));
    }
  }, [account.subscriptions]);

  useEffect(() => {
    async function fetchSchedule() {
      if (!selectedSubscription || account === null) return;
      const { data: response = [] } = await fetchEventAvailablities();
      setEmptySchedule(response.length === 0);
      setTenantName(response[0]?.tenantName || "");
    }
    // if selectedSubscription is undefined, then it means user has no valid subscription to be picked from,
    // the user has to add a membership
    // selectedSubscription is initialized to null
    if (selectedSubscription === undefined) {
      toast.error(i18n.t("screens.Schedule.noEligibleSubscriptions"));
      return;
    }
    fetchSchedule();
  }, [selectedSubscription, account]);

  return (
    <MainContainer title="Schedule">
      <TDCDialog
        visible={TDCDialogVisible}
        setVisible={setTDCDialogVisible}
        cancelText="CLOSE"
      />

      <View style={{ paddingHorizontal: 15, paddingVertical: 10 }}>
        <View
          style={{
            marginBottom: 10,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text style={styles.tenantName}>
            {emptySchedule
              ? i18n.t("screens.Schedule.noEligibleEvents")
              : tenantName}
          </Text>
          <FilterIcon onPress={() => setToggled(!toggled)} />
        </View>
        <Divider />
        <Filters
          toggled={toggled}
          schedule={schedule}
          monthFormat={MONTH_FILTER_FORMAT}
          dayFormat={DAY_FILTER_FORMAT}
        />
        <View style={{ marginTop: 10 }}></View>
        {!isChangingSeats && uniqSubscriptions.length > 1 && (
          <View style={{ marginBottom: 10 }}>
            <PlanPackageSelector
              selectedSubscription={selectedSubscription}
              uniqSubscriptions={uniqSubscriptions}
              onSelect={(selectedSubscription: SubscriptionDetails) =>
                dispatch(setSelectedSubscription(selectedSubscription))
              }
            />
          </View>
        )}
        {emptySchedule && <NoEventsText />}
        {isFetching ? (
          <ActivityIndicator />
        ) : (
          <ScheduleItemList
            schedule={filterSchedule(schedule)}
            selectedSubscription={selectedSubscription}
            planPackageOperable={planPackageOperable}
            setTDCDialog={setTDCDialogVisible}
          />
        )}
      </View>
    </MainContainer>
  );
}

const styles = StyleSheet.create({
  tenantName: {
    fontSize: 26,
    fontWeight: "bold",
  },
});
